import { AxiosInstance } from 'axios';
import {
  createAxiosInstance,
  attachAuthInterceptors,
} from '@/application/helpers/APIRequest';
import apiConfig from '@/application/config/api';

const customerDiscountTierAPI: AxiosInstance = createAxiosInstance({
  baseURL: `${apiConfig.CUSTOMER_AUTH_API_BASE_URL}/customer-discount-tiers`,
  baseUrlKey: 'CUSTOMER_AUTH_API_BASE_URL',
});
attachAuthInterceptors(customerDiscountTierAPI);

// Types
export interface GetCustomerDiscountTiersRequest {
  page?: number;
  limit?: number;
  keyword?: string;
}

export interface CustomerDiscountTierData {
  id: string;
  name: string;
  description: string | null;
  business_profile_id?: string;
  created_at: string;
  updated_at: string;
}

export interface GetCustomerDiscountTiersResponse {
  success: boolean;
  data?: {
    data: CustomerDiscountTierData[];
    total: number;
    limit: number;
    page: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  message?: string;
}

export interface DeleteCustomerDiscountTierResponse {
  success: boolean;
  message?: string;
}

export interface CreateCustomerDiscountTierRequest {
  name: string;
  description?: string;
}

export interface CreateCustomerDiscountTierResponse {
  success: boolean;
  data?: CustomerDiscountTierData;
  message?: string;
}

export interface UpdateCustomerDiscountTierRequest {
  name?: string;
  description?: string;
}

export interface UpdateCustomerDiscountTierResponse {
  success: boolean;
  data?: CustomerDiscountTierData;
  message?: string;
}

export interface GetCustomerDiscountTierResponse {
  success: boolean;
  data?: CustomerDiscountTierData;
  message?: string;
  httpStatus?: number;
}

// API Functions
export const getCustomerDiscountTiers = async (
  params: GetCustomerDiscountTiersRequest
): Promise<GetCustomerDiscountTiersResponse> => {
  try {
    const { page = 1, limit = 10, keyword } = params;
    const queryParams = new URLSearchParams();

    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    if (keyword) {
      queryParams.append('keyword', keyword);
    }

    const response = await customerDiscountTierAPI.get(
      `/?${queryParams.toString()}`
    );

    // Normalize response: API may return array directly or paginated object
    const raw = response.data;
    if (Array.isArray(raw)) {
      return {
        success: true,
        data: {
          data: raw,
          total: raw.length,
          limit,
          page: 1,
          total_pages: 1,
          has_next: false,
          has_previous: false,
        },
      };
    }

    return {
      success: true,
      data: {
        data: raw.data ?? raw.tiers ?? [],
        total: raw.total ?? 0,
        limit: raw.limit ?? limit,
        page: raw.page ?? page,
        total_pages: raw.total_pages ?? 1,
        has_next: raw.has_next ?? false,
        has_previous: raw.has_previous ?? false,
      },
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch customer discount tiers',
    };
  }
};

export const deleteCustomerDiscountTier = async (
  id: string
): Promise<DeleteCustomerDiscountTierResponse> => {
  try {
    await customerDiscountTierAPI.delete(`/${id}`);

    return {
      success: true,
      message: 'Customer discount tier deleted successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to delete customer discount tier',
    };
  }
};

export const createCustomerDiscountTier = async (
  data: CreateCustomerDiscountTierRequest
): Promise<CreateCustomerDiscountTierResponse> => {
  try {
    const response = await customerDiscountTierAPI.post('/', data);

    return {
      success: true,
      data: response.data,
      message: 'Customer discount tier created successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to create customer discount tier',
    };
  }
};

export const updateCustomerDiscountTier = async (
  id: string,
  data: UpdateCustomerDiscountTierRequest
): Promise<UpdateCustomerDiscountTierResponse> => {
  try {
    const response = await customerDiscountTierAPI.put(`/${id}`, data);

    return {
      success: true,
      data: response.data,
      message: 'Customer discount tier updated successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update customer discount tier',
    };
  }
};

export const getCustomerDiscountTier = async (
  id: string,
  opts?: { signal?: AbortSignal }
): Promise<GetCustomerDiscountTierResponse> => {
  try {
    const response = await customerDiscountTierAPI.get(`/${id}`, {
      skipGlobal500ErrorToast: true,
      signal: opts?.signal,
    });

    return {
      success: true,
      data: response.data,
      message: 'Customer discount tier fetched successfully',
    };
  } catch (error: any) {
    const e = error as { code?: string; name?: string };
    if (
      e?.code === 'ERR_CANCELED' ||
      e?.name === 'CanceledError' ||
      e?.name === 'AbortError'
    ) {
      throw error;
    }
    return {
      success: false,
      httpStatus: error.response?.status as number | undefined,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch customer discount tier',
    };
  }
};

export default customerDiscountTierAPI;
